import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { UpdateTablePreferencesRequest, TablePreferencesResponse } from './types';

/**
 * Fetch table preferences for the current user
 */
export function useTablePreferences(tableId: string) {
  return useQuery<TablePreferencesResponse>({
    queryKey: ['table-preferences', tableId],
    queryFn: async () => {
      const { data } = await api.get<TablePreferencesResponse>(`/table-preferences/${tableId}`);
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update table preferences for the current user
 */
export function useUpdateTablePreferences() {
  const queryClient = useQueryClient();

  return useMutation<
    TablePreferencesResponse,
    Error,
    UpdateTablePreferencesRequest,
    { previousPreferences?: TablePreferencesResponse }
  >({
    mutationFn: async (request: UpdateTablePreferencesRequest) => {
      const { data } = await api.put<TablePreferencesResponse>(
        `/table-preferences/${request.tableId}`,
        { columns: request.columns }
      );
      return data;
    },
    onMutate: async (request) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['table-preferences', request.tableId],
      });

      // Snapshot the previous value
      const previousPreferences = queryClient.getQueryData<TablePreferencesResponse>([
        'table-preferences',
        request.tableId,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData<TablePreferencesResponse>(['table-preferences', request.tableId], {
        tableId: request.tableId,
        preferences: request.columns,
      });

      // Return a context object with the snapshotted value
      return { previousPreferences };
    },
    onError: (err, request, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousPreferences) {
        queryClient.setQueryData(
          ['table-preferences', request.tableId],
          context.previousPreferences
        );
      }
    },
    onSettled: (data, error, request) => {
      // Always refetch after error or success to ensure cache is in sync
      queryClient.invalidateQueries({
        queryKey: ['table-preferences', request.tableId],
      });
    },
  });
}

/**
 * Reset table preferences to defaults (delete all preferences)
 */
export function useResetTablePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableId: string) => {
      await api.delete(`/table-preferences/${tableId}`);
    },
    onSuccess: (data, tableId) => {
      queryClient.invalidateQueries({
        queryKey: ['table-preferences', tableId],
      });
    },
  });
}
